/**
 * Project ID Generator Utility
 * Generates unique, hierarchical project IDs for creator assignments
 * 
 * Format: BRAND123_CAMP456_BRIEF789_CREAT101_240115123045678
 * - Brand ID (first 8 chars)
 * - Campaign ID (first 8 chars) 
 * - Brief ID (first 8 chars)
 * - Creator ID (first 8 chars)
 * - Timestamp (YYMMDDHHmmssSSS format - includes milliseconds for uniqueness)
 */

export class ProjectIdGenerator {
  /**
   * Generate a unique project ID for a creator assignment
   * @param {string} brandId - Brand user ID
   * @param {string} campaignId - Campaign ID
   * @param {string} briefId - Brief ID
   * @param {string} creatorId - Creator ID
   * @returns {string} Generated project ID
   */
  static generateProjectId(brandId, campaignId, briefId, creatorId) {
    const sanitize = (value) => {
      if (value === null || value === undefined) return '';
      return String(value).replace(/\s+/g, '').trim();
    };

    const normalizedBrandId = sanitize(brandId);
    const normalizedCampaignId = sanitize(campaignId);
    const normalizedBriefId = sanitize(briefId);
    const normalizedCreatorId = sanitize(creatorId);

    // Validate inputs
    if (!normalizedBrandId || !normalizedCampaignId || !normalizedBriefId || !normalizedCreatorId) {
      throw new Error('All IDs are required to generate project ID');
    }

    // Generate timestamp in YYMMDDHHmmssSSS format for per-assignment uniqueness
    // Includes milliseconds to ensure uniqueness even if assignments are created in the same second
    const now = new Date();
    const timestamp = now.getFullYear().toString().slice(2) +
                     (now.getMonth() + 1).toString().padStart(2, '0') +
                     now.getDate().toString().padStart(2, '0') +
                     now.getHours().toString().padStart(2, '0') +
                     now.getMinutes().toString().padStart(2, '0') +
                     now.getSeconds().toString().padStart(2, '0') +
                     now.getMilliseconds().toString().padStart(3, '0');

    // Extract first 8 characters from each ID, padding with zeros if needed
    const brandShort = normalizedBrandId.slice(0, 8).padEnd(8, '0');
    const campaignShort = normalizedCampaignId.slice(0, 8).padEnd(8, '0');
    const briefShort = normalizedBriefId.slice(0, 8).padEnd(8, '0');
    const creatorShort = normalizedCreatorId.slice(0, 8).padEnd(8, '0');

    return `${brandShort}_${campaignShort}_${briefShort}_${creatorShort}_${timestamp}`;
  }

  /**
   * Parse a project ID to extract its components
   * @param {string} projectId - Project ID to parse
   * @returns {object} Parsed project components
   */
  static parseProjectId(projectId) {
    if (!projectId || typeof projectId !== 'string') {
      throw new Error('Invalid project ID provided');
    }

    const parts = projectId.split('_');
    
    if (parts.length !== 5) {
      throw new Error('Invalid project ID format');
    }

    return {
      brandId: parts[0],
      campaignId: parts[1],
      briefId: parts[2],
      creatorId: parts[3],
      timestamp: parts[4],
      // Helper methods
      getFullBrandId: () => parts[0],
      getFullCampaignId: () => parts[1],
      getFullBriefId: () => parts[2],
      getFullCreatorId: () => parts[3],
      getFormattedDate: () => {
        // Handle both old format (YYMMDDHHmmss) and new format (YYMMDDHHmmssSSS)
        const timestamp = parts[4];
        const year = '20' + timestamp.slice(0, 2);
        const month = timestamp.slice(2, 4);
        const day = timestamp.slice(4, 6);
        return `${year}-${month}-${day}`;
      }
    };
  }

  /**
   * Validate if a project ID has the correct format
   * @param {string} projectId - Project ID to validate
   * @returns {boolean} True if valid format
   */
  static isValidProjectId(projectId) {
    try {
      const parsed = this.parseProjectId(projectId);
      return parsed && parsed.brandId && parsed.campaignId && parsed.briefId && parsed.creatorId && parsed.timestamp;
    } catch (error) {
      return false;
    }
  }

  /**
   * Generate a human-readable project name from project ID
   * @param {string} projectId - Project ID to convert
   * @param {object} names - Object containing human-readable names
   * @returns {string} Human-readable project name
   */
  static getProjectDisplayName(projectId, names = {}) {
    try {
      const parsed = this.parseProjectId(projectId);
      const brandName = names.brandName || parsed.brandId;
      const campaignName = names.campaignName || parsed.campaignId;
      const briefName = names.briefName || parsed.briefId;
      const creatorName = names.creatorName || parsed.creatorId;
      
      return `${creatorName} - ${briefName} (${campaignName})`;
    } catch (error) {
      return projectId; // Fallback to original ID
    }
  }

  /**
   * Normalize a project ID by removing whitespace from every segment
   * @param {string} projectId
   * @returns {string|null}
   */
  static normalizeProjectId(projectId) {
    if (!projectId) return null;
    const normalized = String(projectId).replace(/\s+/g, '').trim();
    return normalized.length > 0 ? normalized : null;
  }

  /**
   * Normalize a generic identifier (campaignId, briefId, etc.)
   * @param {string} value
   * @returns {string|null}
   */
  static normalizeId(value) {
    if (value === null || value === undefined) return null;
    const normalized = String(value).replace(/\s+/g, '').trim();
    return normalized.length > 0 ? normalized : null;
  }

  /**
   * Compare two project IDs, ignoring whitespace differences
   * @param {string} idA
   * @param {string} idB
   * @returns {boolean}
   */
  static areEquivalent(idA, idB) {
    const normalizedA = this.normalizeProjectId(idA);
    const normalizedB = this.normalizeProjectId(idB);
    if (!normalizedA || !normalizedB) return false;
    return normalizedA === normalizedB;
  }

  /**
   * Extract project ID from various notification or data sources
   * @param {object} data - Data object that might contain project ID
   * @returns {string|null} Project ID if found, null otherwise
   */
  static extractProjectId(data) {
    if (!data) return null;
    
    // Check common project ID fields
    const possibleFields = [
      'projectId',
      'project_id', 
      'assignmentId',
      'assignment_id',
      'id'
    ];
    
    for (const field of possibleFields) {
      if (data[field] && this.isValidProjectId(data[field])) {
        return data[field];
      }
    }
    
    return null;
  }

  /**
   * Generate a project ID for testing purposes
   * @param {string} prefix - Optional prefix for testing
   * @returns {string} Test project ID
   */
  static generateTestProjectId(prefix = 'test') {
    const testBrandId = `${prefix}brand123456789`;
    const testCampaignId = `${prefix}camp123456789`;
    const testBriefId = `${prefix}brief123456789`;
    const testCreatorId = `${prefix}creat123456789`;
    
    return this.generateProjectId(testBrandId, testCampaignId, testBriefId, testCreatorId);
  }
}

export default ProjectIdGenerator;


